import { supabase } from '../lib/supabase';

// ============================================
// PROFILE ANALYTICS SERVICE
// ============================================

export const profileAnalyticsService = {
  // Add profile visitor
  async addVisitor(visitorData) {
    const snakeCaseData = {
      player_id: visitorData?.playerId,
      visitor_name: visitorData?.visitorName,
      visitor_type: visitorData?.visitorType,
      visitor_organization: visitorData?.visitorOrganization || null,
      visitor_country: visitorData?.visitorCountry || null,
      visitor_league: visitorData?.visitorLeague || null,
      metadata: visitorData?.metadata || {}
    };

    const { data, error } = await supabase?.from('profile_visitors')?.insert([snakeCaseData])?.select()?.single();

    if (error) throw error;
    return { data: this.visitorToCamelCase(data) };
  },

  // Get visitors for a player
  async getVisitors(playerId, filters = {}) {
    let query = supabase?.from('profile_visitors')?.select('*')?.eq('player_id', playerId)?.order('last_visit_at', { ascending: false });

    if (filters?.visitorType) {
      query = query?.eq('visitor_type', filters?.visitorType);
    }

    if (filters?.limit) {
      query = query?.limit(filters?.limit);
    }

    const { data, error } = await query;
    if (error) throw error;
    return { data: data?.map(this.visitorToCamelCase) };
  },

  // Get profile view stats
  async getViewStats(playerId) {
    const { data, error } = await supabase?.from('profile_view_stats')?.select('*')?.eq('player_id', playerId)?.single();

    if (error && error?.code !== 'PGRST116') throw error;
    return { data: data ? this.statsToCamelCase(data) : null };
  },

  // Get view stats for current user's player profile
  async getViewStatsForCurrentUser() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get user's assigned player profile
    const { data: profile } = await supabase?.from('user_profiles')?.select('assigned_profile_id')?.eq('user_id', user?.id)?.single();

    if (!profile?.assigned_profile_id) {
      return { data: null };
    }

    return this.getViewStats(profile?.assigned_profile_id);
  },

  visitorToCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      playerId: obj?.player_id,
      visitorName: obj?.visitor_name,
      visitorType: obj?.visitor_type,
      visitorOrganization: obj?.visitor_organization,
      visitorCountry: obj?.visitor_country,
      visitorLeague: obj?.visitor_league,
      visitCount: obj?.visit_count,
      lastVisitAt: obj?.last_visit_at,
      createdAt: obj?.created_at,
      metadata: obj?.metadata
    };
  },

  statsToCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      playerId: obj?.player_id,
      totalViews: obj?.total_views,
      clubViews: obj?.club_views,
      scoutViews: obj?.scout_views,
      sportsDirectorViews: obj?.sports_director_views,
      fifaAgentViews: obj?.fifa_agent_views,
      clubScouterViews: obj?.club_scouter_views,
      uniqueVisitors: obj?.unique_visitors,
      uniqueCountries: obj?.unique_countries,
      uniqueLeagues: obj?.unique_leagues,
      lastUpdated: obj?.last_updated,
      createdAt: obj?.created_at
    };
  }
};

// ============================================
// PLAYER MESSAGES SERVICE
// ============================================

export const playerMessagesService = {
  // Get messages for current user
  async getForCurrentUser(filters = {}) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get the player's assigned_profile_id from user_profiles
    const { data: profile } = await supabase
      ?.from('user_profiles')?.select('assigned_profile_id')?.eq('user_id', user?.id)
      ?.single();

    const assignedProfileId = profile?.assigned_profile_id;

    // Build query: match by user_id OR by player_id (for admin-created players)
    let query;
    if (assignedProfileId) {
      // Query messages where user_id matches OR player_id matches assigned profile
      query = supabase
        ?.from('player_messages')?.select('*')
        ?.or(`user_id.eq.${user?.id},player_id.eq.${assignedProfileId}`)
        ?.order('created_at', { ascending: false });
    } else {
      query = supabase
        ?.from('player_messages')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false });
    }

    if (filters?.senderType) {
      query = query?.eq('sender_type', filters?.senderType);
    }

    if (filters?.isRead !== undefined) {
      query = query?.eq('is_read', filters?.isRead);
    }

    if (filters?.isArchived !== undefined) {
      query = query?.eq('is_archived', filters?.isArchived);
    }

    const { data, error } = await query;
    if (error) throw error;
    return { data: data?.map(this.toCamelCase) };
  },

  // Get unread count
  async getUnreadCount() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get the player's assigned_profile_id
    const { data: profile } = await supabase
      ?.from('user_profiles')
      ?.select('assigned_profile_id')
      ?.eq('user_id', user?.id)
      ?.single();

    const assignedProfileId = profile?.assigned_profile_id;

    let query;
    if (assignedProfileId) {
      query = supabase
        ?.from('player_messages')
        ?.select('*', { count: 'exact', head: true })
        ?.or(`user_id.eq.${user?.id},player_id.eq.${assignedProfileId}`)
        ?.eq('is_read', false)
        ?.eq('is_archived', false);
    } else {
      query = supabase
        ?.from('player_messages')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('user_id', user?.id)
        ?.eq('is_read', false)
        ?.eq('is_archived', false);
    }

    const { count, error } = await query;
    if (error) throw error;
    return { count: count || 0 };
  },

  // Mark message as read
  async markAsRead(messageId) {
    const { data, error } = await supabase?.from('player_messages')?.update({ is_read: true, read_at: new Date()?.toISOString() })?.eq('id', messageId)?.select()?.single();

    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  // Archive message
  async archive(messageId) {
    const { data, error } = await supabase?.from('player_messages')?.update({ is_archived: true, archived_at: new Date()?.toISOString() })?.eq('id', messageId)?.select()?.single();

    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  // Send reply to a message (player -> admin)
  async sendReply(messageId, replyText) {
    const { data, error } = await supabase
      ?.from('player_messages')
      ?.update({
        player_reply: replyText,
        player_reply_at: new Date()?.toISOString(),
      })
      ?.eq('id', messageId)
      ?.select()
      ?.single();
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      playerId: obj?.player_id,
      userId: obj?.user_id,
      senderType: obj?.sender_type,
      senderName: obj?.sender_name,
      senderOrganization: obj?.sender_organization,
      senderCountry: obj?.sender_country,
      senderLeague: obj?.sender_league,
      subject: obj?.subject,
      message: obj?.message,
      priority: obj?.priority,
      isRead: obj?.is_read,
      readAt: obj?.read_at,
      isArchived: obj?.is_archived,
      archivedAt: obj?.archived_at,
      playerReply: obj?.player_reply,
      playerReplyAt: obj?.player_reply_at,
      metadata: obj?.metadata,
      createdAt: obj?.created_at,
      updatedAt: obj?.updated_at
    };
  }
};

// ============================================
// ADMIN PROFILE ANALYTICS SERVICE
// ============================================

export const adminProfileAnalyticsService = {
  // Create/update visitor entry (admin only)
  async upsertVisitor(visitorData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const snakeCaseData = {
      player_id: visitorData?.playerId,
      visitor_name: visitorData?.visitorName,
      visitor_type: visitorData?.visitorType,
      visitor_organization: visitorData?.visitorOrganization || null,
      visitor_country: visitorData?.visitorCountry || null,
      visitor_league: visitorData?.visitorLeague || null,
      visit_count: visitorData?.visitCount || 1,
      metadata: visitorData?.metadata || {}
    };

    const { data, error } = await supabase?.from('profile_visitors')?.insert([snakeCaseData])?.select()?.single();

    if (error) throw error;
    return { data: profileAnalyticsService?.visitorToCamelCase(data) };
  },

  // Update visitor
  async updateVisitor(visitorId, updates) {
    const snakeCaseData = {};
    if (updates?.visitCount !== undefined) snakeCaseData.visit_count = updates?.visitCount;
    if (updates?.visitorOrganization !== undefined) snakeCaseData.visitor_organization = updates?.visitorOrganization;
    if (updates?.visitorCountry !== undefined) snakeCaseData.visitor_country = updates?.visitorCountry;
    if (updates?.visitorLeague !== undefined) snakeCaseData.visitor_league = updates?.visitorLeague;
    if (updates?.metadata !== undefined) snakeCaseData.metadata = updates?.metadata;

    const { data, error } = await supabase?.from('profile_visitors')?.update(snakeCaseData)?.eq('id', visitorId)?.select()?.single();

    if (error) throw error;
    return { data: profileAnalyticsService?.visitorToCamelCase(data) };
  },

  // Delete visitor
  async deleteVisitor(visitorId) {
    const { error } = await supabase?.from('profile_visitors')?.delete()?.eq('id', visitorId);

    if (error) throw error;
    return { success: true };
  },

  // Get all visitors (admin view)
  async getAllVisitors(filters = {}) {
    let query = supabase?.from('profile_visitors')?.select('*, player:player_master_profiles(full_name, primary_position)')?.order('last_visit_at', { ascending: false });

    if (filters?.playerId) {
      query = query?.eq('player_id', filters?.playerId);
    }

    if (filters?.visitorType) {
      query = query?.eq('visitor_type', filters?.visitorType);
    }

    const { data, error } = await query;
    if (error) throw error;
    return { data: data?.map(profileAnalyticsService?.visitorToCamelCase) };
  }
};

// ============================================
// ADMIN MESSAGES SERVICE
// ============================================

export const adminMessagesService = {
  // Create message (admin only)
  async create(messageData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const snakeCaseData = {
      player_id: messageData?.playerId,
      sender_type: messageData?.senderType,
      sender_name: messageData?.senderName,
      sender_organization: messageData?.senderOrganization || null,
      sender_country: messageData?.senderCountry || null,
      sender_league: messageData?.senderLeague || null,
      subject: messageData?.subject,
      message: messageData?.message,
      priority: messageData?.priority || 'normal',
      created_by_admin_id: user?.id,
      metadata: messageData?.metadata || {}
    };

    // Only include user_id if provided (admin-created players may not have auth accounts)
    if (messageData?.userId) {
      snakeCaseData.user_id = messageData?.userId;
    }

    console.log('Inserting player_message:', snakeCaseData);

    const { data, error } = await supabase?.from('player_messages')?.insert([snakeCaseData])?.select()?.single();

    if (error) {
      console.error('player_messages insert error:', error);
      throw error;
    }
    return { data: playerMessagesService?.toCamelCase(data) };
  },

  // Update message
  async update(messageId, updates) {
    const snakeCaseData = {};
    if (updates?.subject !== undefined) snakeCaseData.subject = updates?.subject;
    if (updates?.message !== undefined) snakeCaseData.message = updates?.message;
    if (updates?.priority !== undefined) snakeCaseData.priority = updates?.priority;
    if (updates?.senderName !== undefined) snakeCaseData.sender_name = updates?.senderName;
    if (updates?.senderOrganization !== undefined) snakeCaseData.sender_organization = updates?.senderOrganization;
    if (updates?.senderCountry !== undefined) snakeCaseData.sender_country = updates?.senderCountry;
    if (updates?.senderLeague !== undefined) snakeCaseData.sender_league = updates?.senderLeague;
    if (updates?.metadata !== undefined) snakeCaseData.metadata = updates?.metadata;

    const { data, error } = await supabase?.from('player_messages')?.update(snakeCaseData)?.eq('id', messageId)?.select()?.single();

    if (error) throw error;
    return { data: playerMessagesService?.toCamelCase(data) };
  },

  // Delete message
  async delete(messageId) {
    const { error } = await supabase?.from('player_messages')?.delete()?.eq('id', messageId);

    if (error) throw error;
    return { success: true };
  },

  // Get all messages (admin view)
  async getAll(filters = {}) {
    let query = supabase?.from('player_messages')?.select('*, player:player_master_profiles(full_name), user:user_profiles!player_messages_user_id_fkey(full_name, email)')?.order('created_at', { ascending: false });

    if (filters?.playerId) {
      query = query?.eq('player_id', filters?.playerId);
    }

    if (filters?.userId) {
      query = query?.eq('user_id', filters?.userId);
    }

    if (filters?.senderType) {
      query = query?.eq('sender_type', filters?.senderType);
    }

    const { data, error } = await query;
    if (error) throw error;
    return { data: data?.map(playerMessagesService?.toCamelCase) };
  },

  // Get messages by player
  async getByPlayer(playerId) {
    const { data, error } = await supabase?.from('player_messages')?.select('*, user:user_profiles!player_messages_user_id_fkey(full_name, email)')?.eq('player_id', playerId)?.order('created_at', { ascending: false });

    if (error) throw error;
    return { data: data?.map(playerMessagesService?.toCamelCase) };
  }
};